import React, { useState, useCallback } from 'react';
import { 
  Alert, 
  IconButton, 
  Box, 
  Typography, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Edit,
  Delete,
  Search,
  PersonAdd,
  FilterList,
  Visibility,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import type { User, Collector, Team, CreateCollectorRequest } from '../types';
import { useAuth } from '../contexts/AuthContext';
import GenericFilterModal, { FilterField } from '../components/GenericFilterModal';
import { useFilterState } from '../hooks/useFilterState';
import { 
  PageLayout, 
  PageHeader, 
  ScrollableContent,
  FilterChip
} from '../components/Layout/index';
import ViewModal, { ViewField } from '../components/ViewModal';
import { 
  Button, 
  Chip, 
  DataTable,
  useStatusColors,
  ActionButtons
} from '../design-system';
import type { Column } from '../design-system/components/organisms/DataTable/DataTable';


const Users: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Stati locali per i filtri (senza debounce)
  const [localFilters, setLocalFilters] = useState({
    search: '',
    role: '',
    isActive: null as boolean | null,
    teamId: '',
  });
  
  // Stati per la query (con debounce) - inizializzati vuoti per non filtrare all'avvio
  const [queryFilters, setQueryFilters] = useState({
    search: '',
    role: '',
    isActive: null as boolean | null,
    teamId: '',
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [editingCollector, setEditingCollector] = useState<Collector | null>(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingCollector, setViewingCollector] = useState<Collector | null>(null);
  const [formData, setFormData] = useState<CreateCollectorRequest>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: 'FIELD_AGENT',
    teamId: '',
    isActive: true,
  });

  const { hasRole, user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const statusColors = useStatusColors();

  // Hook per gestire lo stato dei filtri
  const { hasActiveFilters, activeFiltersCount } = useFilterState(queryFilters);

  // Query per recuperare i raccoglitori
  const { data: collectorsData, isLoading: collectorsLoading, error: collectorsError } = useQuery({
    queryKey: ['collectors', { page: page + 1, limit: rowsPerPage, ...queryFilters }],
    queryFn: () => apiService.getCollectors({ 
      page: page + 1, 
      limit: rowsPerPage, 
      search: queryFilters.search || undefined,
      role: queryFilters.role || undefined,
      isActive: queryFilters.isActive,
      teamId: queryFilters.teamId || undefined,
    }),
    staleTime: 5 * 60 * 1000, // 5 minuti
  });

  // Query per recuperare i team
  const { data: teamsData, isLoading: teamsLoading } = useQuery({
    queryKey: ['teams'],
    queryFn: () => apiService.getTeams(),
    staleTime: 10 * 60 * 1000, // 10 minuti
  });

  // Configurazione campi per la modale filtri
  const filterFields: FilterField[] = [
    {
      key: 'search',
      label: 'Cerca (Nome, Email)',
      type: 'text',
      icon: <Search />,
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'role',
      label: 'Ruolo',
      type: 'select',
      options: [
        { value: 'ADMIN', label: 'Amministratore' },
        { value: 'SUPERVISOR', label: 'Supervisore' },
        { value: 'OPERATOR', label: 'Operatore' },
        { value: 'FIELD_AGENT', label: 'Agente di Campo' },
      ],
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'isActive',
      label: 'Stato',
      type: 'select',
      options: [
        { value: 'true', label: 'Attivo' },
        { value: 'false', label: 'Inattivo' },
      ],
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'teamId',
      label: 'Team',
      type: 'select',
      options: [
        ...(teamsLoading ? 
          [{ value: '', label: 'Caricamento team...'}] :
          teamsData?.data && Array.isArray(teamsData.data) ? teamsData.data
            .filter(team => team.isActive)
            .map(team => ({
              value: team.id,
              label: team.name
            })) : []),
      ],
      size: { xs: 12, sm: 6 },
    },
  ];

  // Valori correnti dei filtri
  const filterValues = {
    search: localFilters.search,
    role: localFilters.role,
    isActive: localFilters.isActive === null ? '' : localFilters.isActive.toString(),
    teamId: localFilters.teamId,
  };

  // Funzioni per aggiornare i filtri
  const updateFilter = useCallback((key: string, value: any) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // Funzione per applicare i filtri
  const applyFilters = useCallback(() => {
    setQueryFilters(localFilters);
    setPage(0); // Reset alla prima pagina quando si applicano i filtri
  }, [localFilters]);

  // Funzione per pulire i filtri
  const clearFilters = useCallback(() => {
    const emptyFilters = {
      search: '',
      role: '',
      isActive: null as boolean | null,
      teamId: '',
    };
    setLocalFilters(emptyFilters);
    setQueryFilters(emptyFilters);
    setPage(0);
  }, []);

  const createMutation = useMutation({
    mutationFn: (collector: CreateCollectorRequest) => {
      return apiService.createCollector(collector);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collectors'] });
      setOpenDialog(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateCollectorRequest> }) => {
      return apiService.updateCollector(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collectors'] });
      setOpenDialog(false);
      setEditingCollector(null);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => {
      return apiService.deleteCollector(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collectors'] });
    },
  });

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      phone: '',
      role: 'FIELD_AGENT',
      teamId: '',
      isActive: true,
    });
  };

  const handleOpenDialog = (collector?: Collector) => {
    if (collector) {
      setEditingCollector(collector);
      setFormData({
        email: collector.email,
        password: '',
        firstName: collector.firstName,
        lastName: collector.lastName,
        phone: collector.phone || '',
        role: collector.role,
        teamId: collector.teamId || '',
        isActive: collector.isActive,
      });
    } else {
      setEditingCollector(null);
      resetForm();
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCollector(null);
    resetForm();
  };

  const handleOpenViewModal = (collector: Collector) => {
    setViewingCollector(collector);
    setViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setViewingCollector(null);
  };

  const getCollectorViewFields = (collector: Collector): ViewField[] => [
    {
      key: 'firstName',
      label: 'Nome',
      value: collector.firstName,
      type: 'text',
    },
    {
      key: 'lastName',
      label: 'Cognome',
      value: collector.lastName,
      type: 'text',
    },
    {
      key: 'email',
      label: 'Email',
      value: collector.email,
      type: 'text',
    },
    {
      key: 'phone',
      label: 'Telefono',
      value: collector.phone || 'Non specificato',
      type: 'text',
    },
    {
      key: 'role',
      label: 'Ruolo',
      value: collector.role,
      type: 'text',
    },
    {
      key: 'team',
      label: 'Team',
      value: collector.team?.name || 'Nessun team',
      type: 'text',
    },
    {
      key: 'isActive',
      label: 'Stato',
      value: collector.isActive ? 'Attivo' : 'Inattivo',
      type: 'chip',
      chipColor: collector.isActive ? 'success' : 'error',
    },
    {
      key: 'createdAt',
      label: 'Data Creazione',
      value: collector.createdAt,
      type: 'date',
    },
    {
      key: 'updatedAt',
      label: 'Ultima Modifica',
      value: collector.updatedAt,
      type: 'date',
    },
  ];

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (editingCollector) {
      updateMutation.mutate({ id: editingCollector.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Sei sicuro di voler eliminare questo raccoglitore?')) {
      deleteMutation.mutate(id);
    }
  };

  // Le funzioni di paginazione sono gestite direttamente dal DataTable

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'ADMIN': return 'error';
      case 'SUPERVISOR': return 'warning';
      case 'OPERATOR': return 'info';
      case 'FIELD_AGENT': return 'success';
      default: return 'default';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'ADMIN': return 'Amministratore';
      case 'SUPERVISOR': return 'Supervisore';
      case 'OPERATOR': return 'Operatore';
      case 'FIELD_AGENT': return 'Raccoglitore';
      default: return role;
    }
  };

  if (!hasRole('ADMIN')) {
    return (
      <Box>
        <Alert severity="error">
          Accesso negato. Solo gli amministratori possono gestire il team.
        </Alert>
      </Box>
    );
  }

  // Il loading e gli errori sono gestiti direttamente dal DataTable

  const collectors = collectorsData?.data || [];
  const totalCount = collectorsData?.pagination?.total || 0;

  // Definizione colonne per DataTable
  const columns: Column<Collector>[] = [
    {
      id: 'name',
      label: 'Nome',
      minWidth: 200,
      format: (value, collector) => (
        <Typography 
          variant="body2" 
          sx={{
            fontWeight: 500,
            color: 'text.primary',
            fontSize: '0.875rem',
          }}
        >
          {collector.firstName} {collector.lastName}
        </Typography>
      ),
    },
    {
      id: 'email',
      label: 'Email',
      minWidth: 250,
      format: (value) => (
        <Typography 
          variant="body2" 
          sx={{
            color: 'text.secondary',
            fontSize: '0.75rem',
          }}
        >
          {value}
        </Typography>
      ),
    },
    {
      id: 'role',
      label: 'Ruolo',
      minWidth: 150,
      align: 'center',
      format: (value) => (
        <Chip
          label={getRoleLabel(value)}
          variant={getRoleColor(value) as any}
          size="small"
          sx={{
            fontWeight: 500,
          }}
        />
      ),
    },
    {
      id: 'team',
      label: 'Team',
      minWidth: 150,
      format: (value, collector) => (
        <Typography 
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontSize: '0.75rem',
          }}
        >
          {collector.team?.name || '-'}
        </Typography>
      ),
    },
    {
      id: 'isActive',
      label: 'Stato',
      minWidth: 120,
      align: 'center',
      format: (value, collector) => {
        const colors = collector.isActive ? statusColors.active : statusColors.inactive;
        return (
          <Chip
            label={collector.isActive === true ? 'Attivo' : collector.isActive === false ? 'Inattivo' : 'N/A'}
            variant={collector.isActive ? 'default' : 'outlined'}
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: '0.75rem',
              backgroundColor: colors.backgroundColor,
              color: colors.color,
              borderColor: colors.borderColor,
              ...(collector.isActive ? {
                // Chip attivo con alpha
                backgroundColor: colors.backgroundColor,
                color: colors.color,
                borderColor: colors.borderColor,
              } : {
                // Chip inattivo normale
                backgroundColor: colors.backgroundColor,
                color: colors.color,
                borderColor: colors.borderColor,
              }),
            }}
          />
        );
      },
    },
    {
      id: 'actions',
      label: 'Azioni',
      minWidth: 120,
      align: 'right',
      format: (value, collector) => (
        <ActionButtons
          onView={() => handleOpenViewModal(collector)}
          onEdit={() => handleOpenDialog(collector)}
          onDelete={() => handleDelete(collector.id)}
          size="small"
          spacing={1}
        />
      ),
    },
  ];

  return (
    <PageLayout>
      <PageHeader>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4">Utenti</Typography>
          <Box display="flex" gap={2}>
            <FilterChip
              onClick={() => setFilterModalOpen(true)}
              hasActiveFilters={hasActiveFilters}
              activeFiltersCount={activeFiltersCount}
            />
            <Button
              variant="primary"
              startIcon={<PersonAdd />}
              onClick={() => handleOpenDialog()}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Nuovo Utente
            </Button>
          </Box>
        </Box>
      </PageHeader>

      <ScrollableContent>
        <DataTable
          columns={columns}
          data={collectors}
          loading={collectorsLoading}
          error={collectorsError ? 'Errore nel caricamento dei raccoglitori' : undefined}
          page={page}
          rowsPerPage={rowsPerPage}
          totalRows={totalCount}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          emptyMessage="Nessun raccoglitore trovato"
          hover
          striped
        />
      </ScrollableContent>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCollector ? 'Modifica Utente' : 'Nuovo Utente'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Nome"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Cognome"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingCollector}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Telefono"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Ruolo</InputLabel>
                  <Select
                    value={formData.role}
                    onChange={(e) => {
                      const newRole = e.target.value as any;
                      setFormData({ 
                        ...formData, 
                        role: newRole,
                        // Resetta teamId se non è FIELD_AGENT
                        teamId: newRole === 'FIELD_AGENT' ? formData.teamId : ''
                      });
                    }}
                    label="Ruolo"
                    required
                  >
                    <MenuItem value="">Seleziona ruolo</MenuItem>
                    <MenuItem value="ADMIN">Amministratore</MenuItem>
                    <MenuItem value="SUPERVISOR">Supervisore</MenuItem>
                    <MenuItem value="OPERATOR">Operatore</MenuItem>
                    <MenuItem value="FIELD_AGENT">Raccoglitore</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {formData.role === 'FIELD_AGENT' && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Team</InputLabel>
                    <Select
                      value={formData.teamId || ''}
                      onChange={(e) => setFormData({ ...formData, teamId: e.target.value })}
                      label="Team"
                    >
                      <MenuItem value="">Nessun team</MenuItem>
                      {teamsLoading ? (
                        <MenuItem disabled>Caricamento team...</MenuItem>
                      ) : teamsData?.data && Array.isArray(teamsData.data) ? teamsData.data
                          .filter(team => team.isActive) // Solo team attivi
                          .map(team => (
                            <MenuItem key={team.id} value={team.id}>
                              {team.name}
                            </MenuItem>
                          )) : []}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {/* Campo Stato Utente - nascosto per admin */}
              {!(editingCollector && editingCollector.role === 'ADMIN') && (
                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive || false}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        color="primary"
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body1" fontWeight={500}>
                          Stato Utente
                        </Typography>
                        <Chip 
                          label={formData.isActive === true ? 'Attivo' : formData.isActive === false ? 'Disabilitato' : 'N/A'} 
                          color={formData.isActive ? 'success' : 'error'} 
                          size="small"
                        />
                      </Box>
                    }
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {formData.isActive 
                      ? 'L\'utente può accedere al sistema e utilizzare tutte le funzionalità' 
                      : 'L\'utente non può accedere al sistema e non riceverà notifiche'
                    }
                  </Typography>
                </Grid>
              )}
              
              {/* Messaggio informativo per admin */}
              {editingCollector && editingCollector.role === 'ADMIN' && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Amministratore:</strong> Gli amministratori non possono essere disabilitati per garantire l'accesso al sistema.
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseDialog}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={createMutation.isPending || updateMutation.isPending}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              {createMutation.isPending || updateMutation.isPending ? 'Salvataggio...' : (editingCollector ? 'Aggiorna' : 'Crea')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* GenericFilterModal */}
      <GenericFilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        title="Filtri Team"
        fields={filterFields}
        values={filterValues}
        onUpdateValue={updateFilter}
        onApply={applyFilters}
        onClear={clearFilters}
      />

      {/* ViewModal */}
      <ViewModal
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        title="Dettagli Utente"
        fields={viewingCollector ? getCollectorViewFields(viewingCollector) : []}
        actions={
          <Box>
            <Button
              variant="outlined"
              onClick={handleCloseViewModal}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                mr: 1,
              }}
            >
              Chiudi
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                handleCloseViewModal();
                handleOpenDialog(viewingCollector);
              }}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Modifica
            </Button>
          </Box>
        }
      />
    </PageLayout>
  );
};

export default Users;
